import { useCallback, useMemo, useState } from "react";

/**
 * SIMS-8594 — state for a configuration-screen list (the Pattern v2 shell).
 *
 * The shell owns search, status filter, sort, view mode and pagination; the table and card
 * presentations are pure renderers below it. That split is forced by AC1: the design switches
 * views over the SAME record set (`showTable` / `showCards` in the prototype), and TestRail
 * C17595 requires search/filter/sort/page-size to survive the switch — so this state has to
 * outlive whichever presentation is mounted.
 *
 * Behaviour comes from `Roles Pattern v2.dc.html`, not the Payers file: the Payers export has
 * no sort or pagination control at all, even though hand-off §3.1 claims `payers-01-default`
 * covers FR-10. Recorded as a deviation in the story's source manifest. Quoted values:
 *   page sizes 10/25/50/All (L107-111) · defaults name/asc/page 0/size 10 (L474) ·
 *   All => size = row count (L609) · active column toggles, new column starts asc (L721-733) ·
 *   every narrowing change resets to page 0 (L701, L715, L734, L744).
 *
 * Paging is CLIENT-SIDE by decision (2026-08-28): `payer.service.ts` already fetches the whole
 * collection, so "across the whole record set" (AC2/AC4) holds over the full in-memory list
 * rather than a page. Server-side paging for all config screens is a separate ticket.
 */

export type SortDir = "asc" | "desc";
export type ViewMode = "table" | "cards";

/** Page sizes as strings so "all" is a value rather than a magic number. */
export const PAGE_SIZE_OPTIONS = ["10", "25", "50", "all"] as const;
export type PageSize = string;

export interface UseConfigListOptions<T> {
	rows: T[];
	/** Fields the free-text search runs over. AC2: name, description and member-ID pattern. */
	searchableFields: (row: T) => (string | undefined | null)[];
	/** Status used by the status filter. Omit for a list with no status. */
	statusOf?: (row: T) => string;
	/**
	 * SIMS-8634 AC3 — the category ids a row carries. Omit for a screen with no categories.
	 * A row matches when it holds the selected id; the category's own status is irrelevant here,
	 * which is what makes AC5 work (a retired category still returns its payers).
	 */
	categoriesOf?: (row: T) => string[];
	/** Value to sort by for a given column key. */
	sortValue: (row: T, key: string) => unknown;
	defaultSortKey: string;
	defaultSortDir?: SortDir;
	defaultPageSize?: PageSize;
}

export interface UseConfigListResult<T> {
	// view
	view: ViewMode;
	setView: (v: ViewMode) => void;
	// search + filter
	query: string;
	setQuery: (q: string) => void;
	status: string;
	setStatus: (s: string) => void;
	category: string;
	setCategory: (c: string) => void;
	// sort
	sortKey: string;
	sortDir: SortDir;
	toggleSort: (key: string) => void;
	// pagination
	page: number;
	setPage: (p: number) => void;
	pageSize: PageSize;
	setPageSize: (s: PageSize) => void;
	pageSizeOptions: readonly string[];
	pageCount: number;
	// results
	/** The rows to render for the current page (or all of them when size is "all"). */
	visible: T[];
	/** Total matches across the whole record set — AC2's "true total", not the page length. */
	total: number;
	/** Row count before any narrowing, for the filter summary. */
	grandTotal: number;
	/** True when a search or filter is narrowing the list. */
	isNarrowed: boolean;
	/** 1-based first row on the current page, for the pager summary. 0 when empty. */
	rangeStart: number;
	/** 1-based last row on the current page. 0 when empty. */
	rangeEnd: number;
}

/** Status sentinel meaning "no status filter". */
export const STATUS_ALL = "all";

/** Category sentinel meaning "All categories" (design L128). */
export const CATEGORY_ALL = "all";

export function useConfigList<T>({
	rows,
	searchableFields,
	statusOf,
	categoriesOf,
	sortValue,
	defaultSortKey,
	defaultSortDir = "asc",
	defaultPageSize = "10",
}: UseConfigListOptions<T>): UseConfigListResult<T> {
	const [view, setView] = useState<ViewMode>("table");
	const [query, setQueryRaw] = useState("");
	const [status, setStatusRaw] = useState<string>(STATUS_ALL);
	const [category, setCategoryRaw] = useState<string>(CATEGORY_ALL);
	const [sortKey, setSortKey] = useState(defaultSortKey);
	const [sortDir, setSortDir] = useState<SortDir>(defaultSortDir);
	const [page, setPage] = useState(0);
	const [pageSize, setPageSizeRaw] = useState<PageSize>(defaultPageSize);

	// AC6: any change that narrows or reorders the list returns to the first page. Wrapping the
	// setters is what makes that structural rather than something each caller must remember —
	// the bug this replaces is exactly a caller forgetting it (Payers index.tsx:292 sets the
	// search term with no page reset, leaving an out-of-range empty page).
	const setQuery = useCallback((q: string) => {
		setQueryRaw(q);
		setPage(0);
	}, []);

	const setStatus = useCallback((s: string) => {
		setStatusRaw(s);
		setPage(0);
	}, []);

	// AC6 applies to every narrowing control, categories included.
	const setCategory = useCallback((c: string) => {
		setCategoryRaw(c);
		setPage(0);
	}, []);

	const setPageSize = useCallback((s: PageSize) => {
		setPageSizeRaw(s);
		setPage(0);
	}, []);

	const toggleSort = useCallback((key: string) => {
		// Roles L721-733: the active column flips direction; a different column starts ascending.
		setSortKey((prevKey) => {
			setSortDir((prevDir) =>
				prevKey === key && prevDir === "asc" ? "desc" : "asc",
			);
			return key;
		});
		setPage(0);
	}, []);

	const filtered = useMemo(() => {
		const needle = query.trim().toLowerCase();
		return rows.filter((row) => {
			if (statusOf && status !== STATUS_ALL && statusOf(row) !== status) {
				return false;
			}
			if (
				categoriesOf &&
				category !== CATEGORY_ALL &&
				!categoriesOf(row).includes(category)
			) {
				return false;
			}
			if (!needle) {
				return true;
			}
			return searchableFields(row).some((field) =>
				(field ?? "").toLowerCase().includes(needle),
			);
		});
	}, [rows, query, status, statusOf, category, categoriesOf, searchableFields]);

	const sorted = useMemo(() => {
		const dir = sortDir === "asc" ? 1 : -1;
		// Copy first: Array.prototype.sort mutates, and `filtered` is memoised.
		return [...filtered].sort((a, b) => {
			const av = sortValue(a, sortKey);
			const bv = sortValue(b, sortKey);
			if (av == null && bv == null) return 0;
			if (av == null) return 1; // empties last, regardless of direction
			if (bv == null) return -1;
			if (typeof av === "number" && typeof bv === "number") {
				return (av - bv) * dir;
			}
			return (
				String(av).localeCompare(String(bv), undefined, {
					sensitivity: "base",
				}) * dir
			);
		});
	}, [filtered, sortKey, sortDir, sortValue]);

	const total = sorted.length;
	// Roles L609: "all" collapses to a single page holding everything.
	const size = pageSize === "all" ? Math.max(total, 1) : Number(pageSize);
	const pageCount = Math.max(1, Math.ceil(total / size));
	// Clamp rather than trusting `page`: a narrowing change can shrink the list under the
	// current index between renders, and AC6 forbids landing on an out-of-range empty page.
	const safePage = Math.min(page, pageCount - 1);
	const start = safePage * size;

	const visible = useMemo(
		() => sorted.slice(start, start + size),
		[sorted, start, size],
	);

	return {
		view,
		setView,
		query,
		setQuery,
		status,
		setStatus,
		category,
		setCategory,
		sortKey,
		sortDir,
		toggleSort,
		page: safePage,
		setPage,
		pageSize,
		setPageSize,
		pageSizeOptions: PAGE_SIZE_OPTIONS,
		pageCount,
		visible,
		total,
		grandTotal: rows.length,
		isNarrowed:
			query.trim() !== "" || status !== STATUS_ALL || category !== CATEGORY_ALL,
		rangeStart: total === 0 ? 0 : start + 1,
		rangeEnd: Math.min(start + size, total),
	};
}
